import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import API from "../utils/api";
import { useCountry } from "../context/CountryContext";

const BecomeVendor = () => {
  const navigate = useNavigate();
  const { isGhana, config } = useCountry();

  const [form, setForm] = useState({
    fullName: "",
    phone: "",
    experience: "",
    bankName: "",
    accountNumber: "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [existingApp, setExistingApp] = useState(null);

  // 🎨 THEME PER COUNTRY
  const theme = isGhana
    ? {
        bgGradient: "from-[#1a0500] via-[#2a0a00] to-black",
        titleGradient: "from-yellow-400 via-red-400 to-green-400",
        accentText: "text-yellow-400",
        focusRing: "focus:ring-yellow-400",
        submitGradient: "from-yellow-400 via-red-400 to-green-400",
        backBtn: "bg-yellow-500/90 hover:bg-yellow-400",
        bankPlaceholder: "Bank or Mobile Money Name (optional)",
        accountPlaceholder: "Account / MoMo Number (optional)",
        appName: "ProBank Ghana",
        flag: "🇬🇭",
      }
    : {
        bgGradient: "from-[#020617] via-[#071032] to-[#03040b]",
        titleGradient: "from-yellow-400 via-red-400 to-pink-400",
        accentText: "text-yellow-400",
        focusRing: "focus:ring-yellow-400",
        submitGradient: "from-yellow-400 via-red-400 to-pink-400",
        backBtn: "bg-sky-500/90 hover:bg-sky-400",
        bankPlaceholder: "Bank Name (optional)",
        accountPlaceholder: "Bank Account Number (optional)",
        appName: "ProBank",
        flag: "🇳🇬",
      };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await API.get("/users/me");
        // /api/users/me returns { user: {...}, referrals, transactions }
        const userData = res.data.user || res.data;
        if (userData.vendorApplication) {
          setExistingApp(userData.vendorApplication);
        }
      } catch (err) {
        console.error("❌ Failed to fetch profile:", err.response?.data || err.message);
      }
    };
    fetchProfile();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const response = await API.post("/vendors/apply", {
        ...form,
        country: isGhana ? "GH" : "NG",
      });
      setMessage(response.data.message);
      setTimeout(() => navigate("/dashboard"), 2000);
    } catch (err) {
      console.error("Apply error:", err.response?.data || err.message);
      setMessage(
        `❌ ${err.response?.data?.message || "Failed to submit application. Try again."}`
      );
    } finally {
      setLoading(false);
    }
  };

  // Existing application view
  if (existingApp) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-6 bg-gradient-to-br ${theme.bgGradient}`}>

        {/* Country indicator badge */}
        <div className="fixed top-4 right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-700/50 backdrop-blur-md">
          <span className="text-base leading-none">{theme.flag}</span>
          <span className="text-xs text-slate-300 font-medium">{config.name}</span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="max-w-lg w-full p-6 rounded-3xl bg-black/50 backdrop-blur-md border border-white/6 shadow-2xl"
        >
          <div className="flex items-center justify-between mb-4">
            <h1 className={`text-2xl font-extrabold ${theme.accentText}`}>Vendor Application</h1>
            <div className="text-xs text-gray-400">
              Status: <span className="font-semibold text-green-400">{existingApp.status}</span>
            </div>
          </div>

          <div className="space-y-3 text-gray-300">
            <div>
              <span className="text-sm text-gray-400">Full Name:</span>{" "}
              <span className="font-medium">{existingApp.fullName}</span>
            </div>
            <div>
              <span className="text-sm text-gray-400">Phone:</span>{" "}
              <span className="font-medium">{existingApp.phone}</span>
            </div>
            <div>
              <span className="text-sm text-gray-400">Country:</span>{" "}
              <span className="font-medium">
                {existingApp.country === "GH" ? "🇬🇭 Ghana" : existingApp.country === "NG" ? "🇳🇬 Nigeria" : `${theme.flag} ${config.name}`}
              </span>
            </div>
            <div>
              <span className="text-sm text-gray-400">Experience:</span>
              <div className="mt-1 text-sm text-gray-200">{existingApp.experience || "—"}</div>
            </div>
            {existingApp.adminNotes && (
              <div className="mt-2 p-3 rounded-lg bg-red-900/20 border border-red-700 text-sm text-red-300">
                <strong>Admin note:</strong> {existingApp.adminNotes}
              </div>
            )}

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => navigate("/dashboard")}
                className={`flex-1 py-2 rounded-lg ${theme.backBtn} font-semibold text-white`}
              >
                Back to Dashboard
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 rounded-lg bg-white/6 hover:bg-white/8 text-sm text-white"
              >
                Refresh
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Main form
  return (
    <div className={`min-h-screen flex items-center justify-center p-6 bg-gradient-to-br ${theme.bgGradient}`}>

      {/* Country indicator badge */}
      <div className="fixed top-4 right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-700/50 backdrop-blur-md">
        <span className="text-base leading-none">{theme.flag}</span>
        <span className="text-xs text-slate-300 font-medium">{config.name}</span>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.995, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="w-full max-w-2xl rounded-3xl bg-black/50 backdrop-blur-xl border border-white/6 shadow-2xl p-8"
        aria-live="polite"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className={`text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r ${theme.titleGradient}`}>
              Become a Vendor
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              Apply to sell on {theme.appName} — we'll review and get back to you.
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {theme.flag} {config.name}
            </p>
          </div>

          <div className="text-xs text-gray-400 text-right">
            <div>Secure • Verified • Fast</div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <motion.input
            whileFocus={{ scale: 1.01 }}
            type="text"
            name="fullName"
            placeholder="Full Name"
            value={form.fullName}
            onChange={handleChange}
            required
            className={`col-span-1 md:col-span-2 p-4 rounded-xl bg-gradient-to-br from-white/3 to-black/20 border border-white/6 text-white placeholder-gray-400 outline-none focus:ring-2 ${theme.focusRing} transition`}
          />

          <motion.input
            whileFocus={{ scale: 1.01 }}
            type="tel"
            name="phone"
            placeholder="Phone Number"
            value={form.phone}
            onChange={handleChange}
            required
            className={`p-4 rounded-xl bg-gradient-to-br from-white/3 to-black/20 border border-white/6 text-white placeholder-gray-400 outline-none focus:ring-2 ${theme.focusRing} transition`}
          />

          <motion.textarea
            whileFocus={{ scale: 1.01 }}
            name="experience"
            placeholder="Previous vendor / agent experience?"
            value={form.experience}
            onChange={handleChange}
            rows={4}
            className={`col-span-1 md:col-span-2 p-4 rounded-xl bg-gradient-to-br from-white/3 to-black/20 border border-white/6 text-white placeholder-gray-400 outline-none focus:ring-2 ${theme.focusRing} transition`}
          />

          <motion.input
            whileFocus={{ scale: 1.01 }}
            type="text"
            name="bankName"
            placeholder={theme.bankPlaceholder}
            value={form.bankName}
            onChange={handleChange}
            className={`p-4 rounded-xl bg-gradient-to-br from-white/3 to-black/20 border border-white/6 text-white placeholder-gray-400 outline-none focus:ring-2 ${theme.focusRing} transition`}
          />
          <motion.input
            whileFocus={{ scale: 1.01 }}
            type="text"
            name="accountNumber"
            placeholder={theme.accountPlaceholder}
            value={form.accountNumber}
            onChange={handleChange}
            className={`p-4 rounded-xl bg-gradient-to-br from-white/3 to-black/20 border border-white/6 text-white placeholder-gray-400 outline-none focus:ring-2 ${theme.focusRing} transition`}
          />

          <div className="col-span-1 md:col-span-2 flex items-center justify-between gap-4 mt-2">
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 py-4 rounded-2xl bg-gradient-to-r ${theme.submitGradient} text-gray-900 font-bold shadow-xl hover:scale-105 transition disabled:opacity-60`}
            >
              {loading ? "Submitting..." : "Submit Application"}
            </button>

            <button
              type="button"
              onClick={() => {
                setForm({
                  fullName: "",
                  phone: "",
                  experience: "",
                  bankName: "",
                  accountNumber: "",
                });
                setMessage("");
              }}
              className="px-4 py-3 rounded-xl bg-white/6 hover:bg-white/8 text-sm text-white"
            >
              Reset
            </button>
          </div>
        </form>

        <div className="mt-5">
          {message && (
            <motion.p
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className={`text-center text-sm font-semibold ${
                message.startsWith("✅") ? "text-green-400" : "text-red-400"
              }`}
            >
              {message}
            </motion.p>
          )}
        </div>

        <div className="mt-6 text-xs text-gray-500 text-center">
          By submitting you agree to our <span className="text-sky-300">terms</span> and{" "}
          <span className="text-sky-300">privacy</span>.
        </div>
      </motion.div>
    </div>
  );
};

export default BecomeVendor;
